"use client";

import { useEffect, useState } from "react";
import { getCurrentUser } from "@/core/auth/auth-api";
import ExceptionAdapter from "@/core/exceptions/exception-adapter";
import { type AccountType, type AuthUser } from "@/core/auth/types";

interface UseAuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: AuthUser | null;
  accountType: AccountType | null;
  role: string | null;
  permissions: string[];
  nextStep: string | null;
  redirectTo: string | null;
}

const INITIAL_STATE: UseAuthState = {
  isLoading: true,
  isAuthenticated: false,
  user: null,
  accountType: null,
  role: null,
  permissions: [],
  nextStep: null,
  redirectTo: null,
};

export function useAuth(): UseAuthState {
  const [state, setState] = useState<UseAuthState>(INITIAL_STATE);

  useEffect(() => {
    let isActive = true;

    async function loadAuthState() {
      const auth = await ExceptionAdapter.resolve(() => getCurrentUser(), null);

      if (!isActive) {
        return;
      }

      if (!auth) {
        setState({
          ...INITIAL_STATE,
          isLoading: false,
        });
        return;
      }

      setState({
        isLoading: false,
        isAuthenticated: true,
        user: auth.user,
        accountType: auth.accountType,
        role: auth.role,
        permissions: auth.permissions,
        nextStep: auth.nextStep,
        redirectTo: auth.redirectTo,
      });
    }

    void loadAuthState();

    return () => {
      isActive = false;
    };
  }, []);

  return state;
}
